import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Package, Edit, Search, AlertTriangle, Upload, X, Download, FileSpreadsheet, ToggleLeft, ToggleRight, Plus, Printer } from 'lucide-react';
import { formatCurrency } from '../utils/formatters';
import { SearchableSelect } from '../components/ui/searchable-select';
import { printEPL, printToSpecific, getDefaultPrinter, setDefaultPrinter } from '../utils/qzPrint';

const Products = ({ user }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [businessConfig, setBusinessConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('activos');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [bulkUploadDialogOpen, setBulkUploadDialogOpen] = useState(false);
  const [quickCategoryOpen, setQuickCategoryOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  // Quick label print
  const [labelPrintOpen, setLabelPrintOpen] = useState(false);
  const [labelPrintProduct, setLabelPrintProduct] = useState(null);
  const [labelPrintQty, setLabelPrintQty] = useState(1);
  const [labelPrintIncluirPrecio, setLabelPrintIncluirPrecio] = useState(true);
  const [labelPrintLoading, setLabelPrintLoading] = useState(false);
  const [labelPrintPrinters, setLabelPrintPrinters] = useState([]);
  const [labelPrintSelectedPrinter, setLabelPrintSelectedPrinter] = useState('');
  const [labelPrintRemember, setLabelPrintRemember] = useState(true);
  const [labelPrintPendingEpl, setLabelPrintPendingEpl] = useState(null);
  const [labelPrintShowSelector, setLabelPrintShowSelector] = useState(false);
  const [formData, setFormData] = useState({
    codigo_interno: '',
    nombre: '',
    descripcion: '',
    categoria: '',
    marca: '',
    unidad_medida: 'Botella',
    codigo_barras: [],
    imagen: '',
    tipo_producto: 'VENTA',
    costo: '',
    precio_venta: '',
    precio_minimo: '',
    existencia: '',
    minimo: '10',
    maximo: '100',
    referencia: '',
  });

  const canEdit = user?.role === 'superadmin' || user?.role === 'admin' || user?.role === 'inventario';

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchBusinessConfig();
  }, []);

  const fetchBusinessConfig = async () => {
    try {
      const response = await axios.get(`${API}/business-config`);
      setBusinessConfig(response.data);
    } catch (error) {
      console.error('Error al cargar configuración del negocio');
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      toast.error('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      // Filter only active categories
      setCategories(response.data.filter(cat => cat.activa));
    } catch (error) {
      console.error('Error al cargar categorías');
    }
  };

  const handleQuickCreateCategory = async (e) => {
    e.preventDefault();
    const nombre = newCategoryName.trim();
    if (!nombre) {
      toast.error('El nombre de la categoría es requerido');
      return;
    }
    setCreatingCategory(true);
    try {
      const response = await axios.post(`${API}/categories`, { nombre, descripcion: '', activa: true });
      const created = response.data;
      // Recargar lista de categorias activas
      await fetchCategories();
      // Auto-seleccionar la categoria recien creada en el formulario
      setFormData(prev => ({ ...prev, categoria: created.nombre }));
      toast.success(`Categoría "${created.nombre}" creada y seleccionada`);
      setNewCategoryName('');
      setQuickCategoryOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear la categoría');
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    try {
      // Convertir nombre a MAYÚSCULAS
      const nombreMayusculas = formData.nombre.toUpperCase();
      
      // Auto-llenar descripción si está vacía
      const descripcionFinal = formData.descripcion.trim() 
        ? formData.descripcion 
        : nombreMayusculas.toLowerCase().replace(/\b\w/g, c => c.toUpperCase()); // Title Case
      
      await axios.post(`${API}/products`, {
        ...formData,
        nombre: nombreMayusculas,
        descripcion: descripcionFinal,
        costo: parseFloat(formData.costo),
        precio_venta: parseFloat(formData.precio_venta),
        precio_minimo: formData.precio_minimo ? parseFloat(formData.precio_minimo) : null,
        existencia: parseInt(formData.existencia),
        minimo: parseInt(formData.minimo),
        maximo: parseInt(formData.maximo),
      });
      toast.success('Producto creado exitosamente');
      setDialogOpen(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear producto');
    }
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    try {
      // Convertir nombre a MAYÚSCULAS
      const nombreMayusculas = formData.nombre.toUpperCase();
      
      // Si la descripción está vacía, auto-llenar con el nombre en Title Case
      // Si tiene contenido, mantenerla
      let descripcionFinal = formData.descripcion;
      if (!formData.descripcion.trim()) {
        descripcionFinal = nombreMayusculas.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
      }
      
      const updateData = {
        nombre: nombreMayusculas,
        descripcion: descripcionFinal,
        categoria: formData.categoria,
        marca: formData.marca,
        unidad_medida: formData.unidad_medida,
        codigo_barras: formData.codigo_barras,
        imagen: formData.imagen,
        tipo_producto: formData.tipo_producto,
        costo: parseFloat(formData.costo),
        precio_venta: parseFloat(formData.precio_venta),
        precio_minimo: formData.precio_minimo ? parseFloat(formData.precio_minimo) : null,
        existencia: parseInt(formData.existencia),
        minimo: parseInt(formData.minimo),
        maximo: parseInt(formData.maximo),
        referencia: formData.referencia || '',
      };
      await axios.patch(`${API}/products/${editingProduct.id}`, updateData);
      toast.success('Producto actualizado exitosamente');
      setEditDialogOpen(false);
      setEditingProduct(null);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error('Error al actualizar producto');
    }
  };

  const handleToggleProduct = async (productId, currentStatus) => {
    const accion = currentStatus !== false ? 'desactivar' : 'activar';
    if (window.confirm(`¿Esta seguro de ${accion} este producto?`)) {
      try {
        const response = await axios.delete(`${API}/products/${productId}`);
        toast.success(response.data.message);
        fetchProducts();
      } catch (error) {
        toast.error('Error al cambiar estado del producto');
      }
    }
  };

  // ===== Quick Label Print (usa la configuracion de Impresion de Etiquetas) =====
  const openLabelPrintFor = (product) => {
    setLabelPrintProduct(product);
    setLabelPrintQty(1);
    setLabelPrintIncluirPrecio(true);
    setLabelPrintShowSelector(false);
    setLabelPrintPendingEpl(null);
    setLabelPrintOpen(true);
  };

  const handleQuickLabelPrint = async () => {
    if (!labelPrintProduct) return;
    if (labelPrintQty < 1) {
      toast.error('La cantidad debe ser mayor a 0');
      return;
    }
    setLabelPrintLoading(true);
    try {
      const resp = await axios.post(`${API}/labels/generate-epl`, {
        producto_id: labelPrintProduct.id,
        cantidad: labelPrintQty,
        incluir_precio: labelPrintIncluirPrecio,
      });
      const eplCommands = resp.data.epl_commands;
      const result = await printEPL(eplCommands);
      if (result.sent) {
        toast.success(`Impreso (${labelPrintQty}) en: ${result.usedPrinter}`);
        setLabelPrintOpen(false);
        return;
      }
      // Necesita seleccion
      setLabelPrintPrinters(result.printers);
      setLabelPrintSelectedPrinter(result.printers[0]);
      setLabelPrintPendingEpl(eplCommands);
      setLabelPrintRemember(true);
      setLabelPrintShowSelector(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Error al imprimir etiqueta');
    } finally {
      setLabelPrintLoading(false);
    }
  };

  const handleQuickLabelConfirmPrinter = async () => {
    if (!labelPrintSelectedPrinter || !labelPrintPendingEpl) return;
    setLabelPrintLoading(true);
    try {
      await printToSpecific(labelPrintSelectedPrinter, labelPrintPendingEpl);
      if (labelPrintRemember) {
        setDefaultPrinter(labelPrintSelectedPrinter);
        toast.success(`Impreso en: ${labelPrintSelectedPrinter}. Guardada como predeterminada.`);
      } else {
        toast.success(`Impreso en: ${labelPrintSelectedPrinter}`);
      }
      setLabelPrintShowSelector(false);
      setLabelPrintPendingEpl(null);
      setLabelPrintOpen(false);
    } catch (error) {
      toast.error('Error al imprimir: ' + (error.message || 'Error desconocido'));
    } finally {
      setLabelPrintLoading(false);
    }
  };

  const handleEditClick = (product) => {
    setEditingProduct(product);
    setFormData({
      codigo_interno: product.codigo_interno,
      nombre: product.nombre,
      descripcion: product.descripcion || '',
      categoria: product.categoria,
      marca: product.marca || '',
      unidad_medida: product.unidad_medida,
      codigo_barras: Array.isArray(product.codigo_barras) ? product.codigo_barras : (product.codigo_barras ? [product.codigo_barras] : []),
      imagen: product.imagen || '',
      tipo_producto: product.tipo_producto || 'VENTA',
      costo: product.costo.toString(),
      precio_venta: product.precio_venta.toString(),
      precio_minimo: product.precio_minimo ? product.precio_minimo.toString() : '',
      existencia: product.existencia.toString(),
      minimo: product.minimo.toString(),
      maximo: product.maximo.toString(),
      referencia: product.referencia || '',
    });
    if (product.imagen) {
      setImagePreview(product.imagen);
    }
    setEditDialogOpen(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 1MB for products)
      if (file.size > 1024 * 1024) {
        toast.error('La imagen no debe superar 1MB');
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Por favor seleccione una imagen válida');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setFormData({ ...formData, imagen: base64String });
        setImagePreview(base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setFormData({ ...formData, imagen: '' });
    setImagePreview(null);
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/products/bulk/template`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_productos.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Plantilla descargada exitosamente');
    } catch (error) {
      toast.error('Error al descargar plantilla');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingFile(true);
    setUploadResults(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/products/bulk/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadResults(response.data);
      
      if (response.data.errors.length === 0) {
        toast.success(`¡${response.data.created} productos creados exitosamente!`);
      } else {
        toast.warning(`${response.data.created} productos creados, ${response.data.errors.length} con errores`);
      }
      
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cargar archivo');
    } finally {
      setUploadingFile(false);
      event.target.value = ''; // Reset file input
    }
  };

  const handleExportExcel = async () => {
    try {
      toast.info('Generando archivo Excel...');
      
      const response = await axios.get(`${API}/products/export-excel`, {
        responseType: 'blob'
      });
      
      // Crear URL del blob y descargar
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `inventario_completo_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Inventario descargado exitosamente');
    } catch (error) {
      console.error('Error descargando Excel:', error);
      toast.error('Error al descargar el inventario');
    }
  };

  const resetForm = () => {
    setFormData({
      codigo_interno: '',
      nombre: '',
      descripcion: '',
      categoria: '',
      marca: '',
      unidad_medida: 'Botella',
      codigo_barras: [],
      imagen: '',
      tipo_producto: 'VENTA',
      costo: '',
      precio_venta: '',
      precio_minimo: '',
      existencia: '',
      minimo: '10',
      maximo: '100',
      referencia: '',
    });
    setImagePreview(null);
  };

  const filteredProducts = products.filter((p) => {
    const matchesSearch =
      p.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.codigo_interno.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.codigo_barras && (Array.isArray(p.codigo_barras) ? p.codigo_barras.some(b => b.toLowerCase().includes(searchTerm.toLowerCase())) : p.codigo_barras.toLowerCase().includes(searchTerm.toLowerCase()))) ||
      (p.referencia && p.referencia.toLowerCase().includes(searchTerm.toLowerCase())) ||
      p.categoria.toLowerCase().includes(searchTerm.toLowerCase());

    const isActive = p.activo !== false;
    const matchesStatus =
      filterStatus === 'todos' ||
      (filterStatus === 'activos' && isActive) ||
      (filterStatus === 'inactivos' && !isActive);

    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="products-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
        <div>
          <h1 className="text-4xl font-bold text-slate-800">Inventario</h1>
          <p className="text-slate-600 mt-1">Gestiona el inventario de productos</p>
        </div>

        {canEdit && (
          <div className="flex flex-wrap gap-2">
            {/* Export Excel Button */}
            <Button 
              variant="outline" 
              className="border-blue-500 text-blue-600 hover:bg-blue-50"
              onClick={handleExportExcel}
            >
              <Download className="mr-2" size={18} />
              Descargar Excel
            </Button>

            {/* Bulk Upload Dialog */}
            <Dialog open={bulkUploadDialogOpen} onOpenChange={setBulkUploadDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-emerald-500 text-emerald-600 hover:bg-emerald-50">
                  <FileSpreadsheet className="mr-2" size={18} />
                  Carga Masiva
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Carga Masiva de Productos</DialogTitle>
                  <DialogDescription>
                    Sube un archivo Excel con múltiples productos para crearlos en el sistema
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4">
                  {/* Instructions */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="font-semibold text-blue-900 mb-2">Instrucciones:</h3>
                    <ol className="text-sm text-blue-800 space-y-1 list-decimal ml-4">
                      <li>Descarga la plantilla Excel haciendo clic en el botón de abajo</li>
                      <li>Llena la plantilla con los datos de tus productos</li>
                      <li>Asegúrate de que las categorías existan en el sistema</li>
                      <li>Sube el archivo completado</li>
                    </ol>
                  </div>

                  {/* Download Template Button */}
                  <Button
                    onClick={handleDownloadTemplate}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    <Download className="mr-2" size={18} />
                    Descargar Plantilla Excel
                  </Button>

                  {/* Upload File */}
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileUpload}
                      disabled={uploadingFile}
                      className="hidden"
                      id="bulk-upload"
                    />
                    <label
                      htmlFor="bulk-upload"
                      className={`cursor-pointer ${uploadingFile ? 'opacity-50' : ''}`}
                    >
                      <Upload size={48} className="mx-auto text-slate-400 mb-3" />
                      <p className="font-semibold text-slate-700 mb-1">
                        {uploadingFile ? 'Subiendo archivo...' : 'Haz clic para subir el archivo Excel'}
                      </p>
                      <p className="text-sm text-slate-500">
                        Formatos soportados: .xlsx, .xls
                      </p>
                    </label>
                  </div>

                  {/* Upload Results */}
                  {uploadResults && (
                    <div className="border rounded-lg p-4">
                      <h3 className="font-semibold mb-2">Resultados de la Carga:</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Total de filas:</span>
                          <span className="font-semibold">{uploadResults.total}</span>
                        </div>
                        <div className="flex justify-between text-green-600">
                          <span>Productos creados:</span>
                          <span className="font-semibold">{uploadResults.created}</span>
                        </div>
                        {uploadResults.errors.length > 0 && (
                          <div>
                            <div className="flex justify-between text-red-600 mb-2">
                              <span>Errores:</span>
                              <span className="font-semibold">{uploadResults.errors.length}</span>
                            </div>
                            <div className="max-h-40 overflow-y-auto bg-red-50 rounded p-2">
                              {uploadResults.errors.map((error, index) => (
                                <div key={index} className="text-xs text-red-700 mb-1">
                                  Fila {error.row} - {error.product}: {error.error}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={() => {
                      setBulkUploadDialogOpen(false);
                      setUploadResults(null);
                    }}
                  >
                    Cerrar
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Create Product Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-emerald-500 hover:bg-emerald-600" data-testid="create-product-btn">
                  <Package className="mr-2" size={18} />
                  Nuevo Producto
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="create-product-dialog">
                <DialogHeader>
                  <DialogTitle>Crear Nuevo Producto</DialogTitle>
                  <DialogDescription>Complete la información del producto</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateProduct}>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 mb-4">
                  <p className="text-sm text-blue-800">
                    <strong>Código Interno:</strong> Se generará automáticamente (0001, 0002, etc.)
                  </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* ── Columna Izquierda: Identidad del Producto ── */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider border-b pb-1">Información General</h3>
                    
                    <div>
                      <Label htmlFor="nombre">Nombre *</Label>
                      <Input
                        id="nombre"
                        value={formData.nombre}
                        onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                        required
                        data-testid="form-nombre-input"
                      />
                      <p className="text-xs text-slate-400 mt-1">Se guardará en MAYÚSCULAS</p>
                    </div>

                    <div>
                      <Label htmlFor="descripcion">Descripción</Label>
                      <Textarea
                        id="descripcion"
                        value={formData.descripcion}
                        onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                        rows={2}
                        placeholder="Si se deja vacío, se genera desde el nombre"
                        data-testid="form-descripcion-input"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Categoría *</Label>
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <SearchableSelect
                              value={formData.categoria}
                              onValueChange={(value) => setFormData({ ...formData, categoria: value })}
                              options={categories.map(cat => ({ value: cat.nombre, label: cat.nombre }))}
                              placeholder="Seleccionar..."
                              searchPlaceholder="Buscar categoría..."
                              emptyMessage="No hay categorías."
                              testId="form-categoria-select"
                            />
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="shrink-0"
                            onClick={() => { setNewCategoryName(''); setQuickCategoryOpen(true); }}
                            title="Crear nueva categoría"
                            data-testid="form-new-category-btn"
                          >
                            <Plus size={16} />
                          </Button>
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="marca">Marca</Label>
                        <Input
                          id="marca"
                          value={formData.marca}
                          onChange={(e) => setFormData({ ...formData, marca: e.target.value })}
                          data-testid="form-marca-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="referencia">Referencia</Label>
                        <Input
                          id="referencia"
                          value={formData.referencia}
                          onChange={(e) => setFormData({ ...formData, referencia: e.target.value })}
                          placeholder="Ej: REF-001"
                          data-testid="form-referencia-input"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Unidad *</Label>
                        <SearchableSelect
                          value={formData.unidad_medida}
                          onValueChange={(value) => setFormData({ ...formData, unidad_medida: value })}
                          options={[
                            { value: 'Botella', label: 'Botella' },
                            { value: 'Caja', label: 'Caja' },
                            { value: 'Litro', label: 'Litro' },
                            { value: 'Unidad', label: 'Unidad' },
                          ]}
                          placeholder="Seleccionar..."
                          searchPlaceholder="Buscar unidad..."
                          emptyMessage="No encontrada."
                          testId="form-unidad-select"
                        />
                      </div>
                      <div>
                        <Label htmlFor="tipo_producto">Tipo *</Label>
                        <Select 
                          value={formData.tipo_producto} 
                          onValueChange={(value) => {
                            setFormData({ 
                              ...formData, 
                              tipo_producto: value,
                              ...(value === 'SERVICIO' && { costo: '0', existencia: '0' })
                            });
                          }}
                        >
                          <SelectTrigger data-testid="form-tipo-producto-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="VENTA">Producto de Venta</SelectItem>
                            <SelectItem value="SERVICIO">Producto de Servicio</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Imagen */}
                    <div>
                      <Label htmlFor="imagen">Imagen (Opcional)</Label>
                      <Input
                        id="imagen"
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="cursor-pointer text-sm"
                      />
                      {imagePreview && (
                        <div className="mt-2 relative inline-block">
                          <img 
                            src={imagePreview} 
                            alt="Preview" 
                            className="w-24 h-24 object-cover rounded-lg border-2 border-slate-200"
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={removeImage}
                            className="absolute -top-2 -right-2 h-5 w-5 p-0 rounded-full"
                          >
                            <X size={12} />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ── Columna Derecha: Precios, Stock y Códigos ── */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider border-b pb-1">Precios y Stock</h3>

                    {formData.tipo_producto === 'SERVICIO' && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                        <p className="text-xs text-amber-800">
                          <strong>Servicio:</strong> Costo y existencia no aplican.
                        </p>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                      {formData.tipo_producto === 'VENTA' && (
                        <div>
                          <Label htmlFor="costo">Costo *</Label>
                          <Input
                            id="costo"
                            type="number"
                            step="0.01"
                            value={formData.costo}
                            onChange={(e) => setFormData({ ...formData, costo: e.target.value })}
                            required={formData.tipo_producto === 'VENTA'}
                            data-testid="form-costo-input"
                          />
                        </div>
                      )}
                      <div>
                        <Label htmlFor="precio_venta">Precio Venta *</Label>
                        <Input
                          id="precio_venta"
                          type="number"
                          step="0.01"
                          value={formData.precio_venta}
                          onChange={(e) => setFormData({ ...formData, precio_venta: e.target.value })}
                          required
                          data-testid="form-precio-input"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="precio_minimo">Precio Mínimo (Lista General)</Label>
                      <Input
                        id="precio_minimo"
                        type="number"
                        step="0.01"
                        value={formData.precio_minimo}
                        onChange={(e) => setFormData({ ...formData, precio_minimo: e.target.value })}
                        placeholder="Opcional"
                      />
                      <p className="text-xs text-slate-400 mt-1">Mínimo permitido para venta</p>
                    </div>

                    {formData.tipo_producto === 'VENTA' && (
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label htmlFor="existencia">Existencia *</Label>
                          <Input
                            id="existencia"
                            type="number"
                            value={formData.existencia}
                            onChange={(e) => setFormData({ ...formData, existencia: e.target.value })}
                            required={formData.tipo_producto === 'VENTA'}
                            data-testid="form-existencia-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="minimo">Mín *</Label>
                          <Input
                            id="minimo"
                            type="number"
                            value={formData.minimo}
                            onChange={(e) => setFormData({ ...formData, minimo: e.target.value })}
                            required={formData.tipo_producto === 'VENTA'}
                            data-testid="form-minimo-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="maximo">Máx *</Label>
                          <Input
                            id="maximo"
                            type="number"
                            value={formData.maximo}
                            onChange={(e) => setFormData({ ...formData, maximo: e.target.value })}
                            required={formData.tipo_producto === 'VENTA'}
                            data-testid="form-maximo-input"
                          />
                        </div>
                      </div>
                    )}

                    {/* Códigos de Barras */}
                    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider border-b pb-1 pt-1">Códigos de Barras</h3>
                    <div className="space-y-2">
                      {(formData.codigo_barras || []).map((bc, idx) => (
                        <div key={idx} className="flex gap-2 items-center">
                          <Input
                            value={bc}
                            onChange={(e) => {
                              const updated = [...formData.codigo_barras];
                              updated[idx] = e.target.value;
                              setFormData({ ...formData, codigo_barras: updated });
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                e.stopPropagation();
                              }
                            }}
                            placeholder={`Código de barras ${idx + 1}`}
                            data-testid={`form-barcode-input-${idx}`}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="shrink-0 text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8"
                            onClick={() => {
                              const updated = formData.codigo_barras.filter((_, i) => i !== idx);
                              setFormData({ ...formData, codigo_barras: updated });
                            }}
                            data-testid={`form-barcode-remove-${idx}`}
                          >
                            <X size={14} />
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full border-dashed text-xs"
                        onClick={() => setFormData({ ...formData, codigo_barras: [...(formData.codigo_barras || []), ''] })}
                        data-testid="form-barcode-add-btn"
                      >
                        <Plus size={14} className="mr-1" /> Agregar Código de Barras
                      </Button>
                      <p className="text-xs text-slate-400">Opcional - Para escaneo en POS</p>
                    </div>
                  </div>
                </div>

                <Button type="submit" className="w-full bg-emerald-500 hover:bg-emerald-600 mt-5" data-testid="submit-product-btn">
                  Crear Producto
                </Button>
                </form>
              </DialogContent>
            </Dialog>
        </div>
      )}
      </div>

      {/* Search and Filter */}
      <div className="flex gap-3 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
          <Input
            placeholder="Buscar por nombre, código, barras o categoría..."
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="search-products-input"
          />
        </div>
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg" data-testid="product-status-filter">
          <button
            onClick={() => setFilterStatus('activos')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              filterStatus === 'activos' ? 'bg-white text-emerald-700 shadow-sm' : 'text-slate-600 hover:text-slate-800'
            }`}
          >
            Activos
          </button>
          <button
            onClick={() => setFilterStatus('inactivos')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              filterStatus === 'inactivos' ? 'bg-white text-red-700 shadow-sm' : 'text-slate-600 hover:text-slate-800'
            }`}
          >
            Inactivos
          </button>
          <button
            onClick={() => setFilterStatus('todos')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              filterStatus === 'todos' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-600 hover:text-slate-800'
            }`}
          >
            Todos
          </button>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="custom-table">
            <thead>
              <tr>
                <th>Código</th>
                <th>Nombre</th>
                <th>Tipo</th>
                <th>Categoría</th>
                <th>Marca</th>
                <th>Referencia</th>
                <th>Precio</th>
                <th>Existencia</th>
                <th>Estado</th>
                {canEdit && <th>Acciones</th>}
              </tr>
            </thead>
            <tbody data-testid="products-table-body">
              {filteredProducts.map((product) => {
                const isLowStock = product.existencia <= product.minimo;
                const isActive = product.activo !== false;
                return (
                  <tr key={product.id} className={`${!isActive ? 'opacity-50 bg-slate-50' : ''}`} data-testid={`product-row-${product.codigo_interno}`}>
                    <td className="font-medium">{product.codigo_interno}</td>
                    <td>
                      <div>
                        <div className="font-medium">{product.nombre}</div>
                        {product.descripcion && (
                          <div className="text-xs text-slate-500">{product.descripcion}</div>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${
                        product.tipo_producto === 'SERVICIO' 
                          ? 'bg-purple-100 text-purple-700' 
                          : 'bg-emerald-100 text-emerald-700'
                      }`}>
                        {product.tipo_producto === 'SERVICIO' ? 'Servicio' : 'Venta'}
                      </span>
                    </td>
                    <td>
                      <span className="inline-block px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-700">
                        {product.categoria}
                      </span>
                    </td>
                    <td className="text-slate-600">{product.marca || '-'}</td>
                    <td className="text-slate-600">{product.referencia || '-'}</td>
                    <td className="font-semibold text-green-600">
                      {formatCurrency(product.precio_venta)}
                    </td>
                    <td className={isLowStock && product.tipo_producto !== 'SERVICIO' ? 'text-red-600 font-semibold' : ''}>
                      {product.tipo_producto === 'SERVICIO' ? (
                        <span className="text-slate-400">N/A</span>
                      ) : (
                        `${product.existencia} ${product.unidad_medida}`
                      )}
                    </td>
                    <td>
                      {!isActive ? (
                        <span className="inline-block px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-700">
                          Inactivo
                        </span>
                      ) : product.tipo_producto === 'SERVICIO' ? (
                        <span className="inline-block px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-700">
                          Servicio
                        </span>
                      ) : isLowStock ? (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-700">
                          <AlertTriangle size={12} className="mr-1" />
                          Bajo Stock
                        </span>
                      ) : (
                        <span className="inline-block px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-700">
                          Activo
                        </span>
                      )}
                    </td>
                    {canEdit && (
                      <td>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditClick(product)}
                            data-testid={`edit-product-btn-${product.codigo_interno}`}
                          >
                            <Edit size={16} />
                          </Button>
                          {product.tipo_producto !== 'SERVICIO' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-blue-600 border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                              onClick={() => openLabelPrintFor(product)}
                              title="Imprimir etiqueta"
                              data-testid={`print-label-btn-${product.codigo_interno}`}
                            >
                              <Printer size={16} />
                            </Button>
                          )}
                          {(user?.role === 'superadmin' || user?.role === 'admin') && (
                            <Button
                              size="sm"
                              variant={isActive ? 'outline' : 'default'}
                              className={isActive
                                ? 'text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700'
                                : 'bg-emerald-500 hover:bg-emerald-600 text-white'
                              }
                              onClick={() => handleToggleProduct(product.id, product.activo)}
                              data-testid={`toggle-product-btn-${product.codigo_interno}`}
                              title={isActive ? 'Desactivar producto' : 'Activar producto'}
                            >
                              {isActive ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                            </Button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filteredProducts.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            No se encontraron productos
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="edit-product-dialog">
          <DialogHeader>
            <DialogTitle>Editar Producto</DialogTitle>
            <DialogDescription>Modifique la información del producto</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateProduct}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* ── Columna Izquierda: Identidad del Producto ── */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider border-b pb-1">Información General</h3>
                
                <div>
                  <Label>Código Interno</Label>
                  <Input value={formData.codigo_interno} disabled className="bg-slate-50" />
                </div>

                <div>
                  <Label htmlFor="edit_nombre">Nombre *</Label>
                  <Input
                    id="edit_nombre"
                    value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                    required
                    data-testid="edit-nombre-input"
                  />
                  <p className="text-xs text-slate-400 mt-1">Se guardará en MAYÚSCULAS</p>
                </div>

                <div>
                  <Label htmlFor="edit_descripcion">Descripción</Label>
                  <Textarea
                    id="edit_descripcion"
                    value={formData.descripcion}
                    onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                    rows={2}
                    placeholder="Si se deja vacío, se genera desde el nombre"
                    data-testid="edit-descripcion-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Categoría *</Label>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <SearchableSelect
                          value={formData.categoria}
                          onValueChange={(value) => setFormData({ ...formData, categoria: value })}
                          options={categories.map(cat => ({ value: cat.nombre, label: cat.nombre }))}
                          placeholder="Seleccionar..."
                          searchPlaceholder="Buscar categoría..."
                          emptyMessage="No hay categorías."
                          testId="edit-categoria-select"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="shrink-0"
                        onClick={() => { setNewCategoryName(''); setQuickCategoryOpen(true); }}
                        title="Crear nueva categoría"
                        data-testid="edit-new-category-btn"
                      >
                        <Plus size={16} />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="edit_marca">Marca</Label>
                    <Input
                      id="edit_marca"
                      value={formData.marca}
                      onChange={(e) => setFormData({ ...formData, marca: e.target.value })}
                      data-testid="edit-marca-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_referencia">Referencia</Label>
                    <Input
                      id="edit_referencia"
                      value={formData.referencia}
                      onChange={(e) => setFormData({ ...formData, referencia: e.target.value })}
                      placeholder="Ej: REF-001"
                      data-testid="edit-referencia-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Unidad *</Label>
                    <SearchableSelect
                      value={formData.unidad_medida}
                      onValueChange={(value) => setFormData({ ...formData, unidad_medida: value })}
                      options={[
                        { value: 'Botella', label: 'Botella' },
                        { value: 'Caja', label: 'Caja' },
                        { value: 'Litro', label: 'Litro' },
                        { value: 'Unidad', label: 'Unidad' },
                      ]}
                      placeholder="Seleccionar..."
                      searchPlaceholder="Buscar unidad..."
                      emptyMessage="No encontrada."
                      testId="edit-unidad-select"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_tipo_producto">Tipo *</Label>
                    <Select 
                      value={formData.tipo_producto} 
                      onValueChange={(value) => {
                        setFormData({ 
                          ...formData, 
                          tipo_producto: value,
                          ...(value === 'SERVICIO' && { costo: '0', existencia: '0' })
                        });
                      }}
                    >
                      <SelectTrigger data-testid="edit-tipo-producto-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="VENTA">Producto de Venta</SelectItem>
                        <SelectItem value="SERVICIO">Producto de Servicio</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Imagen */}
                <div>
                  <Label htmlFor="edit_imagen">Imagen (Opcional)</Label>
                  <Input
                    id="edit_imagen"
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="cursor-pointer text-sm"
                  />
                  {imagePreview && (
                    <div className="mt-2 relative inline-block">
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        className="w-24 h-24 object-cover rounded-lg border-2 border-slate-200"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={removeImage}
                        className="absolute -top-2 -right-2 h-5 w-5 p-0 rounded-full"
                      >
                        <X size={12} />
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* ── Columna Derecha: Precios, Stock y Códigos ── */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider border-b pb-1">Precios y Stock</h3>

                {formData.tipo_producto === 'SERVICIO' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                    <p className="text-xs text-amber-800">
                      <strong>Servicio:</strong> Costo y existencia no aplican.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  {formData.tipo_producto === 'VENTA' && (
                    <div>
                      <Label htmlFor="edit_costo">Costo *</Label>
                      <Input
                        id="edit_costo"
                        type="number"
                        step="0.01"
                        value={formData.costo}
                        onChange={(e) => setFormData({ ...formData, costo: e.target.value })}
                        required={formData.tipo_producto === 'VENTA'}
                        disabled={businessConfig?.allow_edit_stock_cost === false}
                        className={businessConfig?.allow_edit_stock_cost === false ? 'bg-slate-100 cursor-not-allowed' : ''}
                        data-testid="edit-costo-input"
                      />
                      {businessConfig?.allow_edit_stock_cost === false && (
                        <p className="text-xs text-amber-600 mt-1">Bloqueado. Editar en Compras.</p>
                      )}
                    </div>
                  )}
                  <div>
                    <Label htmlFor="edit_precio_venta">Precio Venta *</Label>
                    <Input
                      id="edit_precio_venta"
                      type="number"
                      step="0.01"
                      value={formData.precio_venta}
                      onChange={(e) => setFormData({ ...formData, precio_venta: e.target.value })}
                      required
                      data-testid="edit-precio-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="edit_precio_minimo">Precio Mínimo (Lista General)</Label>
                  <Input
                    id="edit_precio_minimo"
                    type="number"
                    step="0.01"
                    value={formData.precio_minimo}
                    onChange={(e) => setFormData({ ...formData, precio_minimo: e.target.value })}
                    placeholder="Opcional"
                  />
                  <p className="text-xs text-slate-400 mt-1">Mínimo permitido para venta</p>
                </div>

                {formData.tipo_producto === 'VENTA' && (
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label htmlFor="edit_existencia">Existencia *</Label>
                      <Input
                        id="edit_existencia"
                        type="number"
                        value={formData.existencia}
                        onChange={(e) => setFormData({ ...formData, existencia: e.target.value })}
                        required={formData.tipo_producto === 'VENTA'}
                        disabled={businessConfig?.allow_edit_stock_cost === false}
                        className={businessConfig?.allow_edit_stock_cost === false ? 'bg-slate-100 cursor-not-allowed' : ''}
                        data-testid="edit-existencia-input"
                      />
                      {businessConfig?.allow_edit_stock_cost === false && (
                        <p className="text-xs text-amber-600 mt-1">Bloqueado</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="edit_minimo">Mín *</Label>
                      <Input
                        id="edit_minimo"
                        type="number"
                        value={formData.minimo}
                        onChange={(e) => setFormData({ ...formData, minimo: e.target.value })}
                        required={formData.tipo_producto === 'VENTA'}
                        data-testid="edit-minimo-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit_maximo">Máx *</Label>
                      <Input
                        id="edit_maximo"
                        type="number"
                        value={formData.maximo}
                        onChange={(e) => setFormData({ ...formData, maximo: e.target.value })}
                        required={formData.tipo_producto === 'VENTA'}
                        data-testid="edit-maximo-input"
                      />
                    </div>
                  </div>
                )}

                {/* Códigos de Barras */}
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider border-b pb-1 pt-1">Códigos de Barras</h3>
                <div className="space-y-2">
                  {(formData.codigo_barras || []).map((bc, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <Input
                        value={bc}
                        onChange={(e) => {
                          const updated = [...formData.codigo_barras];
                          updated[idx] = e.target.value;
                          setFormData({ ...formData, codigo_barras: updated });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            e.stopPropagation();
                          }
                        }}
                        placeholder={`Código de barras ${idx + 1}`}
                        data-testid={`edit-barcode-input-${idx}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="shrink-0 text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8"
                        onClick={() => {
                          const updated = formData.codigo_barras.filter((_, i) => i !== idx);
                          setFormData({ ...formData, codigo_barras: updated });
                        }}
                        data-testid={`edit-barcode-remove-${idx}`}
                      >
                        <X size={14} />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full border-dashed text-xs"
                    onClick={() => setFormData({ ...formData, codigo_barras: [...(formData.codigo_barras || []), ''] })}
                    data-testid="edit-barcode-add-btn"
                  >
                    <Plus size={14} className="mr-1" /> Agregar Código de Barras
                  </Button>
                  <p className="text-xs text-slate-400">Opcional - Para escaneo en POS</p>
                </div>
              </div>
            </div>

            <Button type="submit" className="w-full bg-emerald-500 hover:bg-emerald-600 mt-5" data-testid="submit-edit-product-btn">
              Actualizar Producto
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Mini-diálogo: Crear categoría rápida sin salir del modal de producto */}
      <Dialog open={quickCategoryOpen} onOpenChange={setQuickCategoryOpen}>
        <DialogContent className="max-w-sm" data-testid="quick-category-dialog">
          <DialogHeader>
            <DialogTitle>Nueva Categoría</DialogTitle>
            <DialogDescription>
              Crea una categoría y se seleccionará automáticamente en el producto.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleQuickCreateCategory} className="space-y-4">
            <div>
              <Label htmlFor="quick-cat-name">Nombre *</Label>
              <Input
                id="quick-cat-name"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="Ej: Aceites, Filtros..."
                autoFocus
                data-testid="quick-category-name-input"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setQuickCategoryOpen(false)}
                disabled={creatingCategory}
                data-testid="quick-category-cancel-btn"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-emerald-500 hover:bg-emerald-600"
                disabled={creatingCategory || !newCategoryName.trim()}
                data-testid="quick-category-save-btn"
              >
                {creatingCategory ? 'Creando...' : 'Crear y Seleccionar'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ===== Dialog: Imprimir Etiqueta Rapido ===== */}
      <Dialog open={labelPrintOpen} onOpenChange={(open) => { if (!open) { setLabelPrintOpen(false); setLabelPrintShowSelector(false); setLabelPrintPendingEpl(null); } }}>
        <DialogContent className="max-w-md" data-testid="quick-label-print-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer size={20} className="text-blue-600" />
              Imprimir Etiqueta
            </DialogTitle>
            <DialogDescription>
              Usa la configuración guardada en Impresión de Etiquetas
            </DialogDescription>
          </DialogHeader>

          {labelPrintProduct && !labelPrintShowSelector && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-slate-600">Producto:</p>
                <p className="font-bold text-slate-800">{labelPrintProduct.nombre}</p>
                <p className="text-xs text-slate-500">
                  Código: {labelPrintProduct.codigo_interno}
                  {labelPrintProduct.referencia ? ` · Ref: ${labelPrintProduct.referencia}` : ''}
                </p>
                <p className="text-sm font-semibold text-emerald-600 mt-1">
                  {formatCurrency(labelPrintProduct.precio_venta)}
                </p>
              </div>

              <div>
                <Label htmlFor="quick-print-qty">Cantidad de Etiquetas</Label>
                <Input
                  id="quick-print-qty"
                  type="number"
                  min="1"
                  value={labelPrintQty}
                  onChange={(e) => setLabelPrintQty(parseInt(e.target.value) || 1)}
                  autoFocus
                  data-testid="quick-print-qty-input"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="quick-print-incluir-precio"
                  checked={labelPrintIncluirPrecio}
                  onChange={(e) => setLabelPrintIncluirPrecio(e.target.checked)}
                  className="h-4 w-4"
                  data-testid="quick-print-incluir-precio"
                />
                <Label htmlFor="quick-print-incluir-precio" className="cursor-pointer text-sm">
                  Incluir precio (si la etiqueta lo soporta)
                </Label>
              </div>

              {getDefaultPrinter() && (
                <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded p-2">
                  <strong>Impresora predeterminada:</strong> {getDefaultPrinter()}
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setLabelPrintOpen(false)}
                  className="flex-1"
                  disabled={labelPrintLoading}
                  data-testid="quick-print-cancel-btn"
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  onClick={handleQuickLabelPrint}
                  disabled={labelPrintLoading || labelPrintQty < 1}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  data-testid="quick-print-confirm-btn"
                >
                  <Printer className="mr-2" size={16} />
                  {labelPrintLoading ? 'Imprimiendo...' : `Imprimir (${labelPrintQty})`}
                </Button>
              </div>
            </div>
          )}

          {labelPrintShowSelector && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Selecciona la impresora donde imprimir las etiquetas:
              </p>
              <div className="space-y-2">
                {labelPrintPrinters.map((printer, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                      labelPrintSelectedPrinter === printer
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                    onClick={() => setLabelPrintSelectedPrinter(printer)}
                    data-testid={`quick-printer-option-${idx}`}
                  >
                    <Printer size={18} className={labelPrintSelectedPrinter === printer ? 'text-blue-600' : 'text-slate-400'} />
                    <span className="text-sm font-medium">{printer}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="quick-remember-printer"
                  checked={labelPrintRemember}
                  onChange={(e) => setLabelPrintRemember(e.target.checked)}
                  className="h-4 w-4"
                  data-testid="quick-remember-printer-checkbox"
                />
                <Label htmlFor="quick-remember-printer" className="cursor-pointer text-sm">
                  Recordar como impresora predeterminada
                </Label>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setLabelPrintShowSelector(false); setLabelPrintPendingEpl(null); }}
                  className="flex-1"
                  disabled={labelPrintLoading}
                >
                  Atrás
                </Button>
                <Button
                  type="button"
                  onClick={handleQuickLabelConfirmPrinter}
                  disabled={labelPrintLoading || !labelPrintSelectedPrinter}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  data-testid="quick-print-confirm-printer-btn"
                >
                  <Printer className="mr-2" size={16} />
                  {labelPrintLoading ? 'Imprimiendo...' : 'Imprimir'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Products;
